import React from 'react';

import {
  LocationApiResponse,
  CompanyData,
  UserData,
  TurbineData
} from './types';
import CompanyIcon from '@/assets/icons/company.svg?react';
import LinkIcon from '@/assets/icons/link.svg?react';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import UserIcon from '@/assets/icons/user.svg?react';
import { getSearchCompaniesColumns } from '@/components/LocationDetails/searchCompaniesColumns';
import { getSearchUsersColumns } from '@/components/LocationDetails/searchUsersColumns';
import { getTurbinesColumns } from '@/components/LocationDetails/turbinesColumns';
import { getUsersColumns } from '@/components/LocationDetails/usersColumns';
import { TurbineApiData } from '@/types/turbines';

export type ModalType = 'turbine' | 'company' | 'user';

export interface ModalConfig {
  icon: React.ComponentType<any>;
  title: string;
  rows:
    | LocationApiResponse[]
    | CompanyData[]
    | UserData[]
    | TurbineData[]
    | TurbineApiData[];
  columns: any[];
  buttonLabel: string;
  modalButtons: Array<{
    label: string;
    onClick: () => void;
    variant: 'cancelBlue' | 'okBlue';
    btnIcon?: React.ComponentType<any>;
    disabled?: boolean;
    isLoader?: boolean;
  }>;
}

export const getModalConfig = (
  type: ModalType,
  companiesData: CompanyData[],
  usersData: UserData[],
  turbinesData: TurbineApiData[],
  handleCloseModal: () => void,
  selectedTurbines: TurbineApiData[] = [],
  onAssignTurbines?: (turbines: TurbineApiData[]) => void,
  selectedCompanies: Array<{ id: string; name: string }> = [],
  onAssignCompanies?: (companies: Array<{ id: string; name: string }>) => void,
  selectedUsers: Array<{ name: string; username: string }> = [],
  onAssignUsers?: (users: Array<{ name: string; username: string }>) => void
): ModalConfig => {
  switch (type) {
    case 'turbine':
      return {
        icon: TurbineIcon,
        title: 'Select a Turbine',
        rows: turbinesData,
        columns: getTurbinesColumns<TurbineApiData>(false),
        buttonLabel:
          selectedTurbines.length > 0
            ? `ASSIGN ${selectedTurbines.length} TURBINE${selectedTurbines.length > 1 ? 'S' : ''}`
            : 'ASSIGN TURBINE',
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              handleCloseModal();
            },
            variant: 'cancelBlue'
          },
          {
            label:
              selectedTurbines.length > 0
                ? `ASSIGN ${selectedTurbines.length} TURBINE${selectedTurbines.length > 1 ? 'S' : ''}`
                : 'ASSIGN TURBINE',
            onClick: async () => {
              if (selectedTurbines.length > 0 && onAssignTurbines) {
                try {
                  await onAssignTurbines(selectedTurbines);
                } catch (error) {
                  console.error('Error in onAssignTurbines:', error);
                }
              }
            },
            variant: 'okBlue',
            btnIcon: LinkIcon,
            disabled: selectedTurbines.length === 0,
            isLoader: true
          }
        ]
      };
    case 'company':
      return {
        icon: CompanyIcon,
        title: 'Select a Company',
        rows: companiesData,
        columns: getSearchCompaniesColumns<CompanyData>(),
        buttonLabel:
          selectedCompanies.length > 0
            ? `ASSIGN ${selectedCompanies.length} COMPAN${selectedCompanies.length > 1 ? 'IES' : 'Y'}`
            : 'ASSIGN COMPANY',
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              handleCloseModal();
            },
            variant: 'cancelBlue'
          },
          {
            label:
              selectedCompanies.length > 0
                ? `ASSIGN ${selectedCompanies.length} COMPAN${selectedCompanies.length > 1 ? 'IES' : 'Y'}`
                : 'ASSIGN COMPANY',
            onClick: async () => {
              if (selectedCompanies.length > 0 && onAssignCompanies) {
                try {
                  await onAssignCompanies(selectedCompanies);
                } catch (error) {
                  console.error('Error in onAssignCompanies:', error);
                }
              }
            },
            variant: 'okBlue',
            btnIcon: LinkIcon,
            disabled: selectedCompanies.length === 0,
            isLoader: true
          }
        ]
      };
    case 'user':
      return {
        icon: UserIcon,
        title: 'Select a User',
        rows: usersData,
        columns: getSearchUsersColumns<UserData>(),
        buttonLabel:
          selectedUsers.length > 0
            ? `ASSIGN ${selectedUsers.length} USER${selectedUsers.length > 1 ? 'S' : ''}`
            : 'ASSIGN USER',
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              handleCloseModal();
            },
            variant: 'cancelBlue'
          },
          {
            label:
              selectedUsers.length > 0
                ? `ASSIGN ${selectedUsers.length} USER${selectedUsers.length > 1 ? 'S' : ''}`
                : 'ASSIGN USER',
            onClick: async () => {
              if (selectedUsers.length > 0 && onAssignUsers) {
                try {
                  await onAssignUsers(selectedUsers);
                } catch (error) {
                  console.error('Error in onAssignUsers:', error);
                }
              }
            },
            variant: 'okBlue',
            btnIcon: LinkIcon,
            disabled: selectedUsers.length === 0,
            isLoader: true
          }
        ]
      };
    default:
      return {
        icon: UserIcon,
        title: 'Select a User',
        rows: usersData,
        columns: getUsersColumns<UserData>(false),
        buttonLabel: 'ASSIGN',
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => {
              handleCloseModal();
            },
            variant: 'cancelBlue'
          },
          {
            label: 'ASSIGN',
            onClick: () => {},
            variant: 'okBlue',
            btnIcon: LinkIcon
          }
        ]
      };
  }
};

export const getDeleteModalConfig = (
  item: LocationApiResponse | CompanyData | UserData
) => {
  if ('data' in item) {
    return {
      title: 'Location',
      subtitle: item.data.name,
      buttonLabel: 'UNASSIGN LOCATION'
    };
  } else if ('turbines' in item) {
    return {
      title: 'Company',
      subtitle: item.name,
      buttonLabel: 'UNASSIGN COMPANY'
    };
  } else if ('userRole' in item) {
    return {
      title: 'User',
      subtitle: `${item.name} ${item.lastName}`,
      buttonLabel: 'UNASSIGN USER'
    };
  }
  return {
    title: 'Item',
    subtitle: 'Unknown',
    buttonLabel: 'UNASSIGN'
  };
};
